import { useMemo, type VoidFunctionComponent } from 'react';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { useDispatch } from 'react-redux';

import * as IssuesV2Store from '../../../redux/issuesV2';
import { formatTimestamp } from '../../../utils';
import { useRevisionScanIdQueryParam } from '../hooks/useRevisionScanIdQueryParam';
import type { ProjectDashboardRevision } from '../projectDashboardTypes';

import styles from './IssueScanDropdown.module.less';

type Props = {
  revision: ProjectDashboardRevision;
};

const getScanButtonLabel = (scannedAt?: string) => (
  <span className="full">Scanned: {scannedAt ? formatTimestamp(scannedAt) : 'Unknown'}</span>
);

const IssueScanDropdown: VoidFunctionComponent<Props> = ({ revision }) => {
  const dispatch = useDispatch();
  const { revisionScanId, setRevisionScanId } = useRevisionScanIdQueryParam();

  const hasMultipleRevisionScans = (revision.data?.revisionScans?.length ?? 0) > 1;
  //  Note that this "lives" in Reflux, however updating the `revisionScanId` query param is what is actually behind the magic,
  //  as it gets synced way up in the RevisionContainer.
  const lastScannedAtTime = revision.data?.selectedRevisionScan?.scanned_at;

  const revisionScanItems = useMemo(() => {
    if (!revision.data?.revisionScans) {
      return [];
    }
    return revision.data.revisionScans.map(revScan => (
      <MenuItem
        active={revScan.id === revisionScanId}
        eventKey={revScan.id}
        key={revScan.id}
      >
        {formatTimestamp(revScan.scanned_at)}
      </MenuItem>
    ));
  }, [revisionScanId, revision.data?.revisionScans]);

  const onSelect = (nextRevisionScanId?: number) => {
    if (nextRevisionScanId) {
      //  Keep the Redux store updated
      dispatch(IssuesV2Store.actions.setScanId(nextRevisionScanId));
      setRevisionScanId(nextRevisionScanId);
    }
  };

  if (!revisionScanId && !hasMultipleRevisionScans) {
    return null;
  }

  return (
    <DropdownButton
      id="select-issue-scan"
      className={styles.dropdownButton}
      title={getScanButtonLabel(lastScannedAtTime)}
      onSelect={onSelect as any}
    >
      {revisionScanItems}
    </DropdownButton>
  );
};

export default IssueScanDropdown;
